import axios, { type AxiosResponse } from 'axios';
import moment from 'moment';
import { envService } from './envService';
import { logAuthDiagnostic } from '../diagnostics/authDiagnostic';
import { store } from '../../store/store';
import type { PreRegisterResponse } from '../../types/preRegistration';
import type { VisitorTypeResponse } from '../../types/visitorTypesResponse';
import type { Host } from '../../components/common/HostDropdown/hostSelector';
import type { OrgConfig } from '../../types/orgConfig';
import type {
  IdValidationExpiryPayload,
  IdValidationExpiryResponse,
} from '../../types/idValidation';
import type { VideoExpiryPayload, VideoExpiryResponse } from '../../types/videoExpiry';

export type { OrgConfig };

export interface AuthResponse {
  accessToken: string;
  refreshToken?: string;
}

export interface DocumentTemplate {
  id: string;
  name: string;
  skipSigningFlag?: boolean;
  content?: string;
  docTemplateUri: string;
}

export interface VisitPayload {
  fullName: string;
  email: string;
  companyName: string;
  phoneNumber: string;
  hostUserId: string;
  docTemplateId: string;
  id?: string;
  preRegisterVisitId?: string;
  visitorTypeId?: string;
  checkInTime?: string | undefined;
  orgId?: string | undefined;
  siteId?: string | undefined;
  locationId?: string | undefined;
  deviceId?: string | undefined;
  checkinMethod?: string | undefined;
  smartpassPassId?: string | undefined;
  checkinLatitude?: number | undefined;
  checkinLongitude?: number | undefined;
  scheduledCheckInTime?: string | undefined;
  videoWatched?: boolean;
  poeName?: string;
  buildingName?: string;
  parkingLotName?: string;
  poeId?: string;
  buildingId?: string;
  parkingLotId?: string;
  hostName?: string;
  visitorType?: string;
  siteName?: string;
  visitCustomFieldModels?: Array<{
    orgCustomFieldId: string;
    value: string;
    name: string;
  }>;
  preregisterVisitCustomFieldModels?: Array<{
    orgCustomFieldId: string;
    value: string;
    name: string;
  }>;
  /** EN-4557: previous cycle of a multi check-in invitation; the server carries its artifacts forward. */
  referenceVisitId?: string;
  idFrontImageBase64String?: string;
  idBackImageBase64String?: string;
  idValidatedModels?: ValidationModel[];
  offenderValidatedModels?: OffenderModel[];
  [key: string]: unknown;
}

export interface ValidationModel {
  identityId: string;
  identityStatus: string;
}

export interface OffenderModel {
  offenderCheckId: string;
  offenderStatus: string;
}

export interface VisitCreateResponse {
  id: string;
  visitStatus: string;
  [ key: string ]: unknown;
}

export interface OnSiteVisitor {
  id: string;
  fullName: string;
  checkinTime: string;
  visitorType?: string;
  visitorTypeId?: string;
  companyName?: string;
  hostName?: string;
  orgName?: string;
  siteName?: string;
  email?: string;
}
class TouchlessService {
  private AUTH_URL: string;
  private NEW_QR_AUTH_URL: string;
  private ORGCONFIG_URL: string;
  private VISITOR_ORGCONFIG_URL: string;
  public devicesId: string = '';

  constructor() {
    this.AUTH_URL = `${envService.apiUrl}/visitor/authenticate`;
    this.NEW_QR_AUTH_URL = `${envService.apiUrl}/contactless/authenticate/`;
    this.ORGCONFIG_URL = `${envService.apiUrl}/devices/orgconfig`;
    this.VISITOR_ORGCONFIG_URL = `${envService.apiUrl}/visitor/orgconfig`;
  }

  private getAuthHeaders() {
    // Redux store is the primary source; sessionStorage is the fallback
    // (covers edge cases where the store hasn't rehydrated yet).
    const token =
      store.getState().auth?.accessToken ||
      sessionStorage.getItem('accessToken') ||
      '';
    if (!token) {
      logAuthDiagnostic({
        reason: 'get_auth_headers_missing',
        extra: { source: 'touchlessService.getAuthHeaders' },
      });
    }
    return {
      Authorization: token,
    };
  }

  async getAuthenticate(isNewQRUrlParams: boolean, token: string): Promise<AuthResponse> {
    const API_URL = isNewQRUrlParams ? this.NEW_QR_AUTH_URL + token : this.AUTH_URL;
    const response = await axios.post(API_URL, {});
    return response.data;
  }

  async validateQRCode(token: string): Promise<AuthResponse> {
    const headers = { Authorization: token };
    const response = await axios.post(this.AUTH_URL, {}, { headers });
    return response.data;
  }

  async getOrgConfig(targetLanguage?: string): Promise<OrgConfig> {
    const headers = this.getAuthHeaders();
    const params = targetLanguage ? { targetLanguage } : undefined;
    const response = await axios.get(this.ORGCONFIG_URL, { headers, params });
    return response.data;
  }

  async getVisitorOrgConfig(targetLanguage?: string): Promise<OrgConfig> {
    const headers = this.getAuthHeaders();
    const params = targetLanguage ? { targetLanguage } : undefined;
    const response = await axios.get(this.VISITOR_ORGCONFIG_URL, { headers, params });
    return response.data;
  }

  async getUsersDetailsByName(name: string = "visitly", orgConfig: OrgConfig, userGroupId?: string | null | undefined): Promise<Host[]> {
    let param = `/users?status=ACTIVE&q=${name}`;
    const isUserGroupEntitled = orgConfig?.orgProducts?.products?.[0]?.entitlements?.find((element) => element.key === 'USER_GROUP');
    const isUGEntittled = isUserGroupEntitled?.value === 'true' ? true : false;
    
    if (orgConfig?.restrictHostLocationFlag === true) {
      param += `&siteId=${orgConfig?.siteId}`;
    }
    if (!this.isNullUndefinedOrBlank(userGroupId) && isUGEntittled) {
      param += `&userGroupId=${userGroupId}`;
    }
    
    const headers = this.getAuthHeaders();
    const response = await axios.get(envService.apiUrl + param, { headers });
    return response.data?.results;
  }

  async checkIsEvacutionStarted(siteId: string): Promise<AxiosResponse> {
    const headers = this.getAuthHeaders();
    const response = await axios.get(envService.apiUrl + `/evac?siteId=${siteId}&isActive=true`, { headers });
    return response;
  }

  async getNDADocument(orgTemplateId: string = ''): Promise<DocumentTemplate> {
    const headers = this.getAuthHeaders();
    const response = await axios.get(envService.apiUrl + '/doctemplates/' + orgTemplateId, { headers });
    return response.data;
  }

  async getPreregistrationData(): Promise<{[key: string]: unknown;}> {
    const headers = this.getAuthHeaders();
    const response = await axios.get(envService.apiUrl + '/visitor/prefill/preregistered', { headers });
    return response.data;
  }

  async getPreRegistered(name: string): Promise<{accessToken: string, refreshToken?: string, [key: string]: unknown;}> {
    const response = await axios.post(envService.apiUrl + '/visitor/authenticate?tkn=' + name, {});
    return response.data;
  }

  async postDocumentExpiry(payload: Record<string, unknown>): Promise<{ isDocExpired: boolean }> {
    const headers = this.getAuthHeaders();
    const response = await axios.post(envService.apiUrl + '/visits/checkDocExpiry', payload, { headers });
    return response.data;
  }

  // EN-4518: has this visitor's previously scanned ID gone out of its validity
  // window? Mirrors postDocumentExpiry above. A malformed request still returns
  // 200 with isIdValidationExpired: true, so only auth failures reject here.
  async postIdValidationExpiry(payload: IdValidationExpiryPayload): Promise<IdValidationExpiryResponse> {
    const headers = this.getAuthHeaders();
    const response = await axios.post(envService.apiUrl + '/visits/checkIdValidationExpiry', payload, { headers });
    return response.data;
  }

  // EN-4854: is this visitor's earlier safety-video watch still inside the
  // visitor type's reuse window? Same shape as postIdValidationExpiry above.
  async postVideoExpiry(payload: VideoExpiryPayload): Promise<VideoExpiryResponse> {
    const headers = this.getAuthHeaders();
    const response = await axios.post(envService.apiUrl + '/visits/checkVideoExpiry', payload, { headers });
    return response.data;
  }

  async postVisit(payload: VisitPayload): Promise<VisitCreateResponse> {
    const headers = this.getAuthHeaders();
    const response = await axios.post(envService.apiUrl + '/visits', payload, { headers });
    return response.data;
  }

  async postPrefillVisit(payload: VisitPayload): Promise<{ [key: string]: unknown; }> {
    const headers = this.getAuthHeaders();
    const response = await axios.post(envService.apiUrl + '/visitor/prefill', payload, { headers });
    return response.data;
  }

  async patchVisit(payload: Record<string, unknown>, obj: { id: string }): Promise<unknown> {
    const headers = this.getAuthHeaders();
    const response = await axios.patch(envService.apiUrl + '/visits/' + obj.id, payload, { headers });
    return response.data;
  }

  async getPreRegisterVisitor(fullname: string): Promise<AxiosResponse> {
    const headers = this.getAuthHeaders();
    const currentDate = moment().utcOffset(0, true).format('YYYY-MM-DD');
    const sevenDays = moment().add(7, 'days').utc().format('YYYY-MM-DD');

    const Url = "/visit/preregister?fullName=" + fullname + "&scheduleCheckinStartDate=" + currentDate + "&scheduleCheckinEndDate=" + sevenDays + "&limit=100&offset=0&sortBy=scheduledCheckinTime&sort=ASC";
    const response = await axios.get(envService.apiUrl + Url, { headers });
    return response;
  }

  async getPreRegisterById(preregisterId: string): Promise<PreRegisterResponse> {
    const headers = this.getAuthHeaders();
    const response = await axios.get(envService.apiUrl + '/visit/preregister/' + preregisterId, { headers });
    return response.data;
  }

  async getByVisitorTypeId(id: string): Promise<VisitorTypeResponse> {
    const headers = this.getAuthHeaders();
    const response = await axios.get(envService.apiUrl + '/visitortypes/' + id, { headers });
    return response.data;
  }

  async getOnSiteVisitors(query: string, siteId: string, orgId: string): Promise<OnSiteVisitor[]> {
    const headers = this.getAuthHeaders();
    const today = moment().format('YYYY-MM-DD');
    const params = new URLSearchParams({
      limit: '15', offset: '0', sort: 'desc', sortBy: 'checkinTime',
      q: query, siteId, orgId,
      visitStartDate: today, visitEndDate: today,
      isOnSiteFlag: 'true',
    });
    const response = await axios.get(`${envService.apiUrl}/visits?${params}`, { headers });
    return (response.data?.results ?? []).filter((v: OnSiteVisitor) => v.fullName);
  }

  async getSignOutInfo(visitId: string): Promise<unknown> {
    const response = await axios.get(envService.apiUrl + '/visits/signout/' + visitId);
    return response.data;
  }

  async getAllPointOfEntryBySite(siteId: string): Promise<unknown> {
    const response = await axios.get(envService.apiUrl + `/sites/${siteId}/advanced-locations/poe`);
    return response.data;
  }

  async getAllParkingLotBySite(siteId: string): Promise<unknown> {
    const response = await axios.get(envService.apiUrl + `/sites/${siteId}/advanced-locations/parking-availability`);
    return response.data;
  }

  async getAllDestinationBySite(siteId: string): Promise<unknown> {
    const response = await axios.get(envService.apiUrl + `/sites/${siteId}/advanced-locations/buildings`);
    return response.data;
  }

  async postSignOutInfo(
    visitId: string,
    visitSignoutCustomFields?: { id: string; name: string; orgCustomFieldId: string; value: string }[]
  ): Promise<unknown> {
    const headers = this.getAuthHeaders();
    const now = moment().format('YYYY-MM-DDTHH:mm:ss');
    const payload: Record<string, unknown> = { id: visitId, checkoutTime: now };
    if (visitSignoutCustomFields?.length) {
      payload.visitSignoutCustomFields = visitSignoutCustomFields;
    }
    const response = await axios.post(envService.apiUrl + '/visits/signout', payload, { headers });
    return response.data;
  }

  async validateId(payload: Record<string, unknown>): Promise<unknown> {
    const headers = this.getAuthHeaders();
    const response = await axios.post(envService.apiUrl + '/identity/validateId', payload, { headers });
    return response.data;
  }

  isNullUndefinedOrBlank(obj: unknown): boolean {
    if (obj == null || obj === undefined || (obj === '')) {
      return true;
    }
    return false;
  }
  formatDate(format: string): string {
    return moment().format(format);
  }
}

export const touchlessService = new TouchlessService();